// 📄 components/ui/LimiteUsoAlerta.tsx
//
// Alerta de "límite cercano" (proyecto_limites_organizacion, 2026-08-20) —
// hasta ahora el usuario solo se enteraba de un límite cuando ya lo había
// tocado (mensaje de error al intentar crear/invitar). Este componente
// muestra el uso ANTES de eso, en el mismo lugar donde se dispara la
// acción, para que no sea una sorpresa. No reemplaza el enforcement real
// (que sigue viviendo en firestore.rules) — es puramente informativo.
//
// Solo tiene sentido mostrarlo cuando quien mira la pantalla es dueño del
// límite en cuestión (ver comentario de uso en cada caller) — no hay forma
// de leer el límite de OTRO usuario desde el cliente (users/{uid}.allow get
// es self-or-admin), así que si `limite` viene undefined simplemente no
// se sabe y el componente no renderiza nada.
import React from 'react';
import { AlertTriangle, XCircle } from 'lucide-react';

interface LimiteUsoAlertaProps {
  cantidad: number;
  limite: number | undefined;
  etiqueta: string; // ej. "proyectos", "equipos", "miembros"
}

export default function LimiteUsoAlerta({ cantidad, limite, etiqueta }: LimiteUsoAlertaProps) {
  if (limite === undefined || !Number.isFinite(limite)) return null;

  const enLimite = cantidad >= limite;
  const cerca = !enLimite && limite > 0 && cantidad / limite >= 0.8;

  if (!enLimite && !cerca) return null;

  const Icon = enLimite ? XCircle : AlertTriangle;
  const estilos = enLimite
    ? 'bg-red-500/10 border-red-500/30 text-red-400'
    : 'bg-amber-500/10 border-amber-500/30 text-amber-400';

  return (
    <div className={`flex items-start gap-2 border rounded-lg p-3 text-xs ${estilos}`}>
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <span>
        {enLimite
          ? `Llegaste al límite de ${limite} ${etiqueta} — contactá al administrador para ampliarlo.`
          : `Estás usando ${cantidad} de ${limite} ${etiqueta} — te queda poco margen.`}
      </span>
    </div>
  );
}
